import { toast } from 'react-toastify';
import { useAuthentication } from '@context/businessContainer';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Button from '@monofront/design-system/components/Button';
import { useEffect, useState } from 'react';
import { useSynchronizeCarts } from '@hooks/businessContainer';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import logger from '@utils/logger';
import { useProceedCheckout } from '@components/ProceedCheckout/useProceedCheckout';
import useNavigationLoader from '@hooks/useNavigationLoader';
import { useSubscribeInCheckout } from '@monofront/business/hooks/checkout/useSubscribeInCheckout';
import {
  CheckoutAppEnum,
  CheckoutOriginPlatforms,
  ICheckoutProductError,
  RoutesEnum,
} from '@monofront/types';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import CheckoutProductErrorsModal from './CheckoutProductErrorsModal';
import { CHECKOUT_ERRORS } from '@monofront/business/constants';
import { useTranslations } from 'next-intl';
import useLocalizedRoutes from '@hooks/i18n/useLocalizedRoutes';
import { useCartMaxWeight } from '@hooks/useCartMaxWeight';
import useMinimumOrderAmount from '@hooks/useMinimumOrderAmount';

interface IProceedCheckoutProps {
  paymentButtonLabel: string;
  disabledButton: boolean;
  dataTestId: string;
}

const ProceedCheckout: React.FC<IProceedCheckoutProps> = ({
  paymentButtonLabel,
  disabledButton,
  dataTestId,
}) => {
  const router = useRouter();
  const { isLoading: isNavigationLoading } = useNavigationLoader();

  const { authUser, isUserReady } = useAuthentication();
  const { isSubscriptionAvailable } = useSubscribeInCheckout(useAuthentication);

  const {
    shoppingCart,
    shoppingCartId,
    checkoutProductErrors,
    setCheckoutProductErrors,
    deleteProductsFromShoppingCart,
  } = useShoppingCart();
  const hasOnlyGiftItem = Boolean(
    shoppingCart.hasGiftItem && shoppingCart.products.length === 1,
  );
  const {
    forceSynchronizeCarts,
    loading: isCartSyncLoading,
    error,
  } = useSynchronizeCarts();
  const { getPath } = useLocalizedRoutes();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(
    Boolean(router?.query?.isCheckoutProductErrorModalOpen),
  );
  const maxWeight = useCartMaxWeight();

  const tCart = useTranslations('Cart');
  const tErrors = useTranslations('Errors');
  const { minimumOrderAmount } = useMinimumOrderAmount();

  const checkoutProductError = ({ error }) =>
    error === CHECKOUT_ERRORS.STOCK_IS_INSUFFICIENT ||
    error === CHECKOUT_ERRORS.PRODUCT_VARIANT_NOT_EXIST ||
    error === CHECKOUT_ERRORS.PRODUCT_NOT_EXIST;

  const handleErrors = (errors: ICheckoutProductError[]) => {
    if (errors) {
      if (errors?.some(checkoutProductError)) {
        setCheckoutProductErrors(errors.filter(checkoutProductError));
        setIsModalOpen(true);
      } else {
        toast(tErrors('redirectionError'), {
          progressClassName: 'toast-error',
        });
      }
    }
  };

  const {
    proceedCheckout,
    minimumOrderAmountNotReached,
    isLoading: isProceedCheckoutLoading,
  } = useProceedCheckout();

  // Notifications won't be handled for the first checkout release
  /*useEffect(() => {
    if (data?.notifications) {
      toast.info('Votre panier a été actualisé.');
    }
  }, [data?.notifications]);*/

  // checkoutV2
  useEffect(() => {
    if (error) {
      logger.error(error);

      toast(tErrors('redirectionError'), {
        progressClassName: 'toast-error',
      });
    }
  }, [error]);

  const isLoading =
    isCartSyncLoading ||
    isProceedCheckoutLoading ||
    isNavigationLoading ||
    !isUserReady;

  return (
    <>
      {hasOnlyGiftItem ? (
        <Link href={getPath(RoutesEnum.ESSENTIALS_COLLECTION)} legacyBehavior>
          <a data-testid="ProceedCheckout-go-to-complete-cart">
            <Button hasMinWidth={false}>{tCart('iCompleteTheCart')}</Button>
          </a>
        </Link>
      ) : authUser ? (
        isLoading ? (
          <div className="heart-container">
            <HeartLoader />
          </div>
        ) : (
          <Button
            hasMinWidth={false}
            disabled={disabledButton}
            onClick={proceedCheckout}
            dataTestId={dataTestId}
          >
            {paymentButtonLabel}
          </Button>
        )
      ) : shoppingCart.totalPrice < minimumOrderAmount &&
        isSubscriptionAvailable ? (
        <Button
          hasMinWidth={false}
          disabled={shoppingCart.totalWeight >= maxWeight}
          onClick={minimumOrderAmountNotReached}
          data-testid={dataTestId}
        >
          {tCart('validateAndPay')}
        </Button>
      ) : (
        <Link
          href={
            isSubscriptionAvailable
              ? '/checkout/register'
              : '/account/register/flow'
          }
          legacyBehavior
        >
          <a
            className="start-subscription"
            rel="nofollow"
            data-testid={dataTestId}
          >
            <Button
              hasMinWidth={false}
              disabled={shoppingCart.totalWeight >= maxWeight}
            >
              {isSubscriptionAvailable ? (
                tCart('validateAndPay')
              ) : (
                <>{tCart('startTrialAndContinue')}</>
              )}
            </Button>
          </a>
        </Link>
      )}
      <CheckoutProductErrorsModal
        isOpen={isModalOpen}
        onValidate={() => {
          setIsModalOpen(false);
          deleteProductsFromShoppingCart(
            shoppingCartId,
            checkoutProductErrors.map((error) => ({
              sku: error.sku,
            })),
          );
          forceSynchronizeCarts(shoppingCart.products, {
            checkoutOrigin: CheckoutAppEnum.WEB,
            checkoutOriginPlatform: CheckoutOriginPlatforms.WEB,
          }).then(async ({ cart, errors }) => {
            if (cart) await router.push('/checkout/delivery');
            else if (errors) handleErrors(errors);
          });
        }}
        onClose={() => setIsModalOpen(false)}
        checkoutProductErrors={checkoutProductErrors}
        shoppingCart={shoppingCart}
      />
      <style jsx>{`
        .member-link {
          margin-bottom: var(--space-3);
          text-align: center;
        }

        .heart-container :global(.beating-heart) {
          margin: auto auto;
        }

        a :global(button) {
          white-space: nowrap;
          line-height: initial;
        }
        @media (max-width: 800px) {
          .start-trial :global(button),
          .start-subscription :global(button) {
            white-space: initial;
          }
        }
      `}</style>
    </>
  );
};

export default ProceedCheckout;
